import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Link, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { FetchReservationDetails, UpdateState } from "../../apis/Reservation";
import { GenerateShortToken } from "../../apis/Auth";
import { ChevronLeft, MoveRight } from "lucide-react";
import { Container } from "reactstrap";
import { Shield } from "lucide-react";
import { fetchMessages } from "../../apis/MessagesApi";

const ManageReservation = () => {
  const [reservation, setReservation] = useState();
  const [totalNightsPrice, setTotalNightsPrice] = useState(0);
  const [messages, setMessages] = useState([]);
  const { reservationId } = useParams();
  const [currentPage, setCurrentPage] = useState("0");
  const limit = "10";
  const chatList = useRef(null);
  const [state, setState] = useState("");
  const [shortToken, setShortToken] = useState("");
  const tokenTimestampRef = useRef(0);
  const generatingPromiseRef = useRef(null);
  const TOKEN_TTL_MS = 3 * 60 * 1000;

  const getValidShortToken = async () => {
    if (
      shortToken &&
      tokenTimestampRef.current &&
      Date.now() - tokenTimestampRef.current < TOKEN_TTL_MS
    ) {
      return shortToken;
    }
    if (generatingPromiseRef.current) {
      try {
        await generatingPromiseRef.current;
        return shortToken;
      } catch {
        return "";
      }
    }
    generatingPromiseRef.current = (async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
        return token;
      } catch (err) {
        toast.error("Failed to generate short token");
        return "";
      } finally {
        generatingPromiseRef.current = null;
      }
    })();
    const newToken = await generatingPromiseRef.current;
    return newToken || "";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await FetchReservationDetails(reservationId);

        if (response) {
          console.log("setting data");
          setReservation(response.data?.reservation);
          setState(response.data?.reservation?.reservationState);
        }
      } catch (error) {
        toast.error("Failed to fetch data");
        console.error(error);
      }
    };
    fetchData();
  }, [reservationId]);

  const loadOldChats = async () => {
    if (chatList.current) {
      if (
        chatList.current.scrollTop + chatList.current.clientHeight >=
        chatList.current.scrollHeight - 1
      ) {
        await FetchMessages();
      }
    }
  };

  useEffect(() => {
    const chatListEl = chatList.current;
    if (chatListEl) {
      chatListEl.addEventListener("scroll", loadOldChats);
      return () => chatListEl.removeEventListener("scroll", loadOldChats);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const FetchMessages = async () => {
    try {
      const nextPage = parseInt(currentPage) + 1;
      console.log("loading page", nextPage.toString());
      setCurrentPage(nextPage.toString());
      const response = await fetchMessages({
        limit: limit,
        currentPage: nextPage.toString(),
        channelName:
          "private-channel-" +
          reservation.guest?._id +
          "-" +
          reservation.host?._id +
          "-" +
          reservation._id,
      });
      console.log("response.messages", response.messages);
      setMessages((prev) => [...prev, ...response.messages]);
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    if (reservation) {
      let total = 0;
      reservation?.nightsPrices?.forEach((nightsPrice) => {
        total += nightsPrice.price;
      });
      setTotalNightsPrice(total);
      FetchMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation]);

  const updateState = (state) => {
    UpdateState(reservation._id, state)
      .then(() => {
        setState(state);
        toast.success(`Reservation ${state} successfully`);
      })
      .catch(() => {
        toast.error("Something went wrong!");
      });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container
          //  h-[calc(100vh-130px)]
          className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb] "
          fluid
        >
          <div className="p-6 flex flex-col gap-6 ">
            {reservation ? (
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 flex flex-col min-w-[300px] lg:min-w-[400px] text-lg gap-4">
                  {(state === "approved" || state === "declined") && (
                    <div className="flex items-center gap-2">
                      <ChevronLeft size={20} className="text-secondaryColor" />
                      <Link to={"/reservation"}>
                        Continue managing reservations
                      </Link>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mx-auto">
                    <img
                      src={"/user.png"}
                      height={40}
                      width={40}
                      alt={"user-icon"}
                      className="rounded-full"
                    />
                    <div className="flex flex-col gap-1 items-center">
                      <div>
                        Booked by{" "}
                        <span className="text-secondaryColor">
                          {reservation.guest?.firstName}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Shield className="mt-0.5" size={20} />{" "}
                        {reservation.guest?.isEmailVerified ? 1 : 0}{" "}
                        Verifications
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <hr className="dark:border-t-white flex-1" />
                    Request to book{" "}
                    <span className="h-1 w-1 rounded-full bg-black"></span>
                    {reservation.checkIn?.toString().slice(0, 10)}
                    <hr className="dark:border-t-white flex-1" />
                  </div>
                  <div
                    ref={chatList}
                    className="max-h-[500px] w-full flex flex-col gap-4 overflow-auto"
                  >
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex items-start gap-2 w-full ${
                          message.host === message.sender
                            ? "flex-row-reverse pl-20"
                            : "flex-row pr-20"
                        }`}
                      >
                        <img
                          src={"/user.png"}
                          height={40}
                          width={40}
                          alt={"user-icon"}
                          className="rounded-full"
                        />
                        <div className="flex flex-col gap-2 p-3 bg-gray-200 dark:bg-darkBg2 w-full">
                          {message.content}
                          <span className="text-base text-gray-600 dark:text-gray-400">
                            {message.updatedAt?.toString().slice(0, 10)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-1 flex flex-col gap-2 h-full">
                  {(state === "pending" && (
                    <div className="flex flex-col border rounded-md text-lg  border-[rgba(0,0,0,0.2)] p-6 m-1 dark:border-[rgba(255,255,255,0.2)]">
                      <h5 className=" font-bold dark:text-[#c7d8eb] first-letter:uppercase">
                        {reservation.guest?.firstName +
                          " " +
                          reservation.guest?.lastName}{" "}
                        has sent a booking request for your listing.
                      </h5>
                      <div className="mt-1">
                        Let{" "}
                        {reservation.guest?.firstName +
                          " " +
                          reservation.guest?.lastName}{" "}
                        know these dates are available by pre-approving them.
                      </div>
                      <div className="mt-1">
                        Respond within 24h to maintain your response rate.
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => updateState("approved")}
                          className=" text-white py-2 px-4 bg-primaryColor hover:bg-primaryColorMuted rounded-md"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateState("declined")}
                          className=" text-primaryColorMuted py-2 px-4 border rounded-md "
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  )) ||
                    (state === "approved" && (
                      <div className="flex flex-col border rounded-md text-lg  border-[rgba(0,0,0,0.2)] p-6 m-1 dark:border-[rgba(255,255,255,0.2)]">
                        <h5 className=" font-bold dark:text-[#c7d8eb] first-letter:uppercase">
                          Booking is confirmed
                        </h5>
                        <div className="mt-1">
                          You can contact your guest and do necessary
                          arrangements or you can cancel.
                        </div>
                      </div>
                    )) ||
                    (state === "declined" && (
                      <div className="flex flex-col border rounded-md text-lg  border-[rgba(0,0,0,0.2)] p-6 m-1 dark:border-[rgba(255,255,255,0.2)]">
                        <h5 className=" font-bold dark:text-[#c7d8eb] first-letter:uppercase">
                          Request declined
                        </h5>
                        <div className="mt-1">
                          Make sure your calendar is up-to-date and that your
                          listing page accurately reflects your house rules and
                          requirements.
                        </div>
                      </div>
                    ))}
                  <div className="flex-1 flex h-full flex-col p-1">
                    <div className="flex flex-col gap-6 overflow-y-auto rounded-md border border-[rgba(0,0,0,0.2)] p-6 dark:border-[rgba(255,255,255,0.2)]">
                      {/* Reservation Info */}
                      <div className="flex flex-col gap-3">
                        <h4 className="font-semibold dark:text-[#c7d8eb]">
                          Reservation Info
                        </h4>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Code</h5>
                          <h5 className="dark:text-gray-400 font-semibold">
                            #{reservation.code}
                          </h5>
                        </div>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Payment ID</h5>
                          <h5 className="dark:text-gray-400 text-sm break-all">
                            {reservation.paymentId}
                          </h5>
                        </div>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Created</h5>
                          <h5 className="dark:text-gray-400">
                            {new Date(reservation.createdAt).toLocaleString()}
                          </h5>
                        </div>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Updated</h5>
                          <h5 className="dark:text-gray-400">
                            {new Date(reservation.updatedAt).toLocaleString()}
                          </h5>
                        </div>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Seen</h5>
                          <h5 className="dark:text-gray-400">
                            <span
                              className={`px-2 py-1 rounded text-sm ${
                                reservation.seen
                                  ? "bg-green-500 text-white"
                                  : "bg-gray-300 text-gray-700"
                              }`}
                            >
                              {reservation.seen ? "Yes" : "No"}
                            </span>
                          </h5>
                        </div>
                      </div>
                      <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />

                      {/* Trip Details */}
                      <div className="flex flex-col gap-3">
                        <h4 className="font-semibold dark:text-[#c7d8eb]">
                          Trip Details
                        </h4>
                        <h5 className="text-secondaryColor">
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={async () => {
                              const token = await getValidShortToken();
                              if (token && reservation.property?._id) {
                                const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${reservation.property._id}?token=${token}`;
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            {reservation.property?.title}
                          </button>
                        </h5>
                        <div className="flex justify-between">
                          <div className="flex w-[45%] flex-col gap-1">
                            <h5 className="text-sm font-semibold dark:text-[#c7d8eb]">
                              Check-in
                            </h5>
                            <h5 className="text-gray-600 dark:text-gray-400">
                              {reservation.checkIn?.toString().slice(0, 10)}
                            </h5>
                          </div>
                          <div className="flex w-1/5 items-center justify-start">
                            <div className="flex size-[30px] items-center justify-center rounded-full bg-gray-200 p-2">
                              <MoveRight className="text-primaryColor size-5" />
                            </div>
                          </div>
                          <div className="flex w-[35%] flex-col gap-1">
                            <h5 className="text-sm font-semibold dark:text-[#c7d8eb]">
                              Check-out
                            </h5>
                            <h5 className="text-gray-600 dark:text-gray-400">
                              {reservation.checkOut?.toString().slice(0, 10)}
                            </h5>
                          </div>
                        </div>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Guests</h5>
                          <h5 className="dark:text-gray-400">
                            {reservation.guests} guests
                          </h5>
                        </div>
                        {reservation.message && (
                          <div className="flex justify-between gap-3">
                            <h5 className="dark:text-gray-400">
                              Guest Message
                            </h5>
                            <h5 className="dark:text-gray-400 text-sm text-right">
                              "{reservation.message}"
                            </h5>
                          </div>
                        )}
                      </div>
                      <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />

                      {/* States */}
                      <div className="flex flex-col gap-3">
                        <h4 className="font-semibold dark:text-[#c7d8eb]">
                          Status
                        </h4>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">
                            Reservation State
                          </h5>
                          <h5 className="dark:text-gray-400 capitalize">
                            <span
                              className={`px-2 py-1 rounded text-sm font-semibold ${
                                state === "pending"
                                  ? "bg-yellow-500 text-white"
                                  : state === "approved"
                                    ? "bg-green-500 text-white"
                                    : state === "declined"
                                      ? "bg-red-500 text-white"
                                      : state === "completed"
                                        ? "bg-blue-500 text-white"
                                        : state === "cancelled"
                                          ? "bg-gray-500 text-white"
                                          : "bg-purple-500 text-white"
                              }`}
                            >
                              {state}
                            </span>
                          </h5>
                        </div>
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Payment State</h5>
                          <h5 className="dark:text-gray-400 capitalize">
                            <span
                              className={`px-2 py-1 rounded text-sm font-semibold ${
                                reservation.paymentState === "pending"
                                  ? "bg-yellow-500 text-white"
                                  : reservation.paymentState === "completed"
                                    ? "bg-green-500 text-white"
                                    : "bg-red-500 text-white"
                              }`}
                            >
                              {reservation.paymentState}
                            </span>
                          </h5>
                        </div>
                        {reservation.reservationState === "cancelled" && (
                          <div className="flex justify-between gap-3">
                            <h5 className="dark:text-gray-400">
                              Cancel Reason
                            </h5>
                            <h5 className="dark:text-gray-400 text-sm text-right">
                              {reservation.cancelReason || "N/A"}
                            </h5>
                          </div>
                        )}
                        <div className="flex justify-between gap-3">
                          <h5 className="dark:text-gray-400">Refund Status</h5>
                          <h5 className="dark:text-gray-400 capitalize">
                            <span
                              className={`px-2 py-1 rounded text-sm ${
                                reservation.refund === "completed"
                                  ? "bg-green-500 text-white"
                                  : reservation.refund === "proceedRefund"
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-400 text-white"
                              }`}
                            >
                              {reservation.refund}
                            </span>
                          </h5>
                        </div>
                      </div>
                      <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />

                      {/* Payment Breakdown */}
                      <div className="flex flex-col gap-3">
                        <h4 className="font-semibold dark:text-[#c7d8eb]">
                          Payment Breakdown
                        </h4>
                        {reservation.nightsPrices?.map((nightPrice, index) => (
                          <div
                            key={index}
                            className="flex justify-between gap-3"
                          >
                            <h5 className="dark:text-gray-400">
                              Night {index + 1}: ${nightPrice.price} x{" "}
                              {nightPrice.nbrNights}
                            </h5>
                            <h5 className="dark:text-gray-400 font-semibold">
                              ${nightPrice.total}
                            </h5>
                          </div>
                        ))}
                      </div>
                      <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />

                      {/* Fees Breakdown */}
                      {reservation.fees && reservation.fees.length > 0 && (
                        <>
                          <div className="flex flex-col gap-3">
                            <h4 className="font-semibold dark:text-[#c7d8eb]">
                              Fees
                            </h4>
                            {reservation.fees.map((fee, index) => (
                              <div
                                key={index}
                                className="flex justify-between gap-3"
                              >
                                <h5 className="dark:text-gray-400">
                                  {fee.feeType}
                                  {fee.valueType === "percentage" &&
                                  fee.percentageValue
                                    ? ` (${fee.percentageValue}%)`
                                    : ""}
                                </h5>
                                <h5 className="dark:text-gray-400 font-semibold">
                                  ${fee.amount}
                                </h5>
                              </div>
                            ))}
                          </div>
                          <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />
                        </>
                      )}

                      {/* Discount & Coupon */}
                      {(reservation.discount || reservation.coupon) && (
                        <>
                          <div className="flex flex-col gap-3">
                            <h4 className="font-semibold dark:text-[#c7d8eb]">
                              Discounts
                            </h4>
                            {reservation.discount && (
                              <div className="flex justify-between gap-3">
                                <h5 className="dark:text-gray-400">Discount</h5>
                                <h5 className="dark:text-gray-400 font-semibold">
                                  -${reservation.discount}
                                </h5>
                              </div>
                            )}
                            {reservation.coupon && (
                              <>
                                <div className="flex justify-between gap-3">
                                  <h5 className="dark:text-gray-400">
                                    Coupon Code
                                  </h5>
                                  <h5 className="dark:text-gray-400 font-semibold">
                                    {reservation.coupon.code}
                                  </h5>
                                </div>
                                <div className="flex justify-between gap-3">
                                  <h5 className="dark:text-gray-400">
                                    Coupon Discount
                                    {reservation.coupon.discountType ===
                                    "percentage"
                                      ? ` (${reservation.coupon.discountValue}%)`
                                      : ""}
                                  </h5>
                                  <h5 className="dark:text-gray-400 font-semibold">
                                    -${reservation.coupon.discountAmount}
                                  </h5>
                                </div>
                              </>
                            )}
                          </div>
                          <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />
                        </>
                      )}

                      {/* Repetition */}
                      {reservation.repetition && (
                        <>
                          <div className="flex flex-col gap-3">
                            <h4 className="font-semibold dark:text-[#c7d8eb]">
                              Repetition
                            </h4>
                            <div className="flex justify-between gap-3">
                              <h5 className="dark:text-gray-400">Total</h5>
                              <h5 className="dark:text-gray-400">
                                {reservation.repetition.total}
                              </h5>
                            </div>
                            <div className="flex justify-between gap-3">
                              <h5 className="dark:text-gray-400">Date</h5>
                              <h5 className="dark:text-gray-400">
                                {new Date(
                                  reservation.repetition.date,
                                ).toLocaleString()}
                              </h5>
                            </div>
                          </div>
                          <hr className="dark:border-t-[rgba(255,255,255,0.2)]" />
                        </>
                      )}

                      {/* Total */}
                      <div className="flex justify-between gap-3 font-semibold">
                        <h4 className="font-semibold dark:text-[#c7d8eb]">
                          Total Amount
                        </h4>
                        <h5 className="dark:text-gray-400 text-lg">
                          ${reservation.totalPrice}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>Loading</>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ManageReservation;
